import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Shield, User } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { navigationLinks } from '../mock/data';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setIsMobileMenuOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        isScrolled ? 'shadow-md' : 'shadow-sm border-b border-gray-100'
      }`}
      data-testid="site-header"
    >
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 mr-4 lg:mr-8" aria-label="Accueil CarMaintenance">
            <img
              src="/logo-carmaintenance.svg"
              alt="CarMaintenance"
              className="h-10 lg:h-12 w-auto transition-transform duration-300 hover:scale-[1.03]"
            />
          </Link>

          {/* Desktop Navigation — single line, no wrap */}
          <nav className="hidden xl:flex items-center gap-6 2xl:gap-8 flex-1 justify-center" aria-label="Navigation principale">
            {navigationLinks.map((link) => {
              const active = location.pathname === link.href;
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`relative text-sm font-medium whitespace-nowrap transition-colors py-1 ${
                    active ? 'text-[#52a675]' : 'text-gray-800 hover:text-[#52a675]'
                  }`}
                  data-testid={`nav-${link.href}`}
                >
                  {link.name}
                  <span
                    className={`absolute left-0 -bottom-1 h-[2px] bg-[#52a675] transition-all duration-300 ${
                      active ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                    aria-hidden="true"
                  />
                </Link>
              );
            })}
          </nav>

          {/* Right side — account links + CTA */}
          <div className="hidden xl:flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-1 pr-3 border-r border-gray-200">
              <Link
                to="/mon-abonnement"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:text-[#52a675] hover:bg-[#52a675]/5 transition-colors"
                title="Espace abonné"
                data-testid="header-mon-abonnement-button"
              >
                <User className="w-4 h-4" />
                <span>Mon abonnement</span>
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:text-[#52a675] hover:bg-[#52a675]/5 transition-colors"
                title="Connexion (admin ou partenaire garage)"
                data-testid="header-login-button"
              >
                <Shield className="w-4 h-4" />
                <span>Connexion</span>
              </Link>
            </div>
            <Link to="/abonnements" data-testid="header-cta-subscribe">
              <Button className="bg-[#52a675] hover:bg-[#3d8a5c] text-white shadow-sm hover:shadow-md transition-all duration-300 font-semibold">
                S'abonner maintenant
              </Button>
            </Link>
          </div>

          {/* Burger (visible below xl breakpoint) */}
          <button
            type="button"
            className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-gray-800 hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMobileMenuOpen}
            data-testid="header-burger"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div
            className="xl:hidden border-t border-gray-100 bg-white animate-in slide-in-from-top-2 duration-200"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col py-3" aria-label="Navigation mobile">
              {navigationLinks.map((link) => {
                const active = location.pathname === link.href;
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`px-2 py-3 text-sm font-medium rounded-md transition-colors ${
                      active
                        ? 'text-[#52a675] bg-[#52a675]/5'
                        : 'text-gray-800 hover:text-[#52a675] hover:bg-gray-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}

              <div className="mt-2 pt-3 border-t border-gray-100 flex flex-col gap-2">
                <Link
                  to="/mon-abonnement"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center gap-2 px-2 py-2.5 text-sm font-medium text-gray-800 rounded-md hover:bg-gray-50"
                  data-testid="header-mobile-mon-abonnement-button"
                >
                  <User className="w-4 h-4 text-[#52a675]" /> Mon abonnement
                </Link>
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center gap-2 px-2 py-2.5 text-sm font-medium text-gray-800 rounded-md hover:bg-gray-50"
                  data-testid="header-mobile-login-button"
                >
                  <Shield className="w-4 h-4 text-[#52a675]" /> Connexion
                </Link>
                <Link to="/abonnements" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="bg-[#52a675] hover:bg-[#3d8a5c] text-white w-full font-semibold">
                    S'abonner maintenant
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
